import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import MediaQuery from 'react-responsive';
import { trans } from '@spotahome/soyuz/client';
import { Button } from '@spotahome/ui-library';
import '@spotahome/ui-library/lib/components/Button.css';
import '@spotahome/ui-library/lib/components/Badge/Badge.css';
import Metrics from '@spotahome/soyuz-metrics';

import AlertButton from '@search/components/AlertButton';
import SortBy from '@search/components/SortBy';

import {
  MQ_MOBILE_MIN_WIDTH,
  MQ_SMALL_MOBILE_MAX_WIDTH
} from '@common/constants';

import { Consumer } from '@search/IsUserLoggedContext';
import { shouldAvoidParamInTheUrl as paramHasDefaultValue } from '@utils/search-url-generator';

import './FiltersHeader.scss';

const excludedFiltersFromCount = [
  'sortBy',
  'poi',
  'area',
  'seoMode',
  'page',
  'poiLat',
  'poiLng',
  'poiName',
  'poiId',
  'show-modal',
  'overrideExperiment',
  'overrideExperiment[]',
  'utm_source',
  'utm_medium',
  'utm_campaign'
];

const getActiveFiltersCount = filters =>
  Object.entries(filters).filter(
    ([key, value]) =>
      !!value &&
      !excludedFiltersFromCount.includes(key) &&
      !paramHasDefaultValue(key, value) &&
      (!Array.isArray(value) || value.length > 0)
  ).length;

const FiltersHeader = ({
  expandedFilters,
  totalResults,
  handleSortChange,
  orderBy,
  citySlug,
  filters,
  handleFiltersToggle
}) => {
  const [filtersCount, setFiltersCount] = useState(0);

  useEffect(() => {
    setFiltersCount(getActiveFiltersCount(filters));
  }, [filters]);

  const closeFiltersText = trans('search.filters.view_close_filters', {
    nResults: totalResults
  });
  const buttonText = trans('search.filter_header.more_filters');
  const buttonTextShort = trans('search.filter_header.more_filters.short');

  const classes = classNames('filter-header', 'search-actions', {
    'search-actions--expanded': expandedFilters,
    'filter-header--expanded': expandedFilters
  });

  const buttonContainerClasses = classNames('filter-header__button-container', {
    'filter-header__button-container--filters-collapsed': !expandedFilters
  });

  const resultButtonClasses = classNames(
    'filter-header__results-button',
    'ga-search-form-more-filters',
    {
      'filter-header__results-button--filters-collapsed': !expandedFilters
    }
  );

  const handleOnClick = () => {
    if (expandedFilters) {
      Metrics.ga.sendEvent('Search', 'Alert Secondary CTA', 'click button');
    } else {
      Metrics.ga.sendEvent('Search', 'Alert Primary CTA', 'click button');
    }
  };

  return (
    <div className={classes}>
      <SortBy handleSortByChange={handleSortChange} orderBy={orderBy} />
      <div className={buttonContainerClasses}>
        <Consumer>
          {({ allCities, isUserLogged }) => (
            <AlertButton
              cityId={citySlug}
              filters={filters}
              allCities={allCities}
              isUserLogged={isUserLogged}
              expandedFilters={expandedFilters}
              onClick={handleOnClick}
            />
          )}
        </Consumer>
        <Button
          className={resultButtonClasses}
          onClick={handleFiltersToggle}
          badge={!expandedFilters && filtersCount ? `${filtersCount}` : ''}
          data-cy="more-filters-button"
        >
          <MediaQuery minWidth={MQ_MOBILE_MIN_WIDTH}>
            {expandedFilters ? closeFiltersText : buttonText}
          </MediaQuery>
          <MediaQuery maxWidth={MQ_SMALL_MOBILE_MAX_WIDTH}>
            {expandedFilters ? closeFiltersText : buttonTextShort}
          </MediaQuery>
        </Button>
      </div>
    </div>
  );
};

FiltersHeader.propTypes = {
  totalResults: PropTypes.number.isRequired,
  handleFiltersToggle: PropTypes.func.isRequired,
  citySlug: PropTypes.string.isRequired,
  expandedFilters: PropTypes.bool,
  orderBy: PropTypes.string,
  handleSortChange: PropTypes.func,
  filters: PropTypes.shape({
    'bathrooms[]': PropTypes.arrayOf(PropTypes.string),
    bed: PropTypes.string,
    budget: PropTypes.string,
    'features[]': PropTypes.arrayOf(PropTypes.string),
    'move-in': PropTypes.string,
    'move-out': PropTypes.string,
    poi: PropTypes.string,
    'topology[]': PropTypes.arrayOf(PropTypes.string),
    'type[]': PropTypes.arrayOf(PropTypes.string),
    'rentalType[]': PropTypes.arrayOf(PropTypes.string)
  }).isRequired
};

FiltersHeader.defaultProps = {
  expandedFilters: false,
  handleSortChange: () => {},
  orderBy: undefined
};
export default FiltersHeader;
